// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import styles from '@app/features/app/components/layout/MobileBottomNav.module.css';
import {reportSkeletonVoiceConnected} from '@app/features/app/components/skeleton/SkeletonLayoutMemory';
import {useSkeletonLayoutReport} from '@app/features/app/hooks/useSkeletonLayoutMemoryCapture';
import Channels from '@app/features/channel/state/Channels';
import Guilds from '@app/features/guild/state/Guilds';
import {PRIMARY_NAVIGATION_LANDMARK_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import * as NavigationCommands from '@app/features/navigation/commands/NavigationCommands';
import Navigation from '@app/features/navigation/state/Navigation';
import SelectedGuild from '@app/features/navigation/state/SelectedGuild';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import {createNamedLoadableComponent} from '@app/features/platform/components/loadable/LoadableComponent';
import {useLocation} from '@app/features/platform/components/router/RouterReact';
import QuickSwitcher from '@app/features/search/state/QuickSwitcher';
import {
	TAB_ACTIVITY_DESCRIPTOR,
	TAB_CHATS_DESCRIPTOR,
	TAB_SEARCH_DESCRIPTOR,
	TAB_TODOS_DESCRIPTOR,
} from '@app/features/tasks/utils/TaskMessageDescriptors';
import type {User} from '@app/features/user/models/User';
import {useConnectedVoiceSession} from '@app/features/voice/hooks/useConnectedVoiceSession';
import {ChannelTypes} from '@fluxer/constants/src/ChannelConstants';
import {Trans, useLingui} from '@lingui/react/macro';
import {
	BellSimpleIcon,
	ChatCircleIcon,
	CheckSquareIcon,
	MagnifyingGlassIcon,
	SpeakerHighIcon,
} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useRef, useState} from 'react';

function resolveHomeGuildId(): string | null {
	const guildIds = Guilds.getGuildIds();
	const preferred = SelectedGuild.selectedGuildId ?? SelectedGuild.lastSelectedGuildId;
	return preferred && guildIds.includes(preferred) ? preferred : (guildIds[0] ?? null);
}

const VoiceLobbyBottomSheet = createNamedLoadableComponent<Record<string, unknown>>({
	displayName: 'VoiceLobbyBottomSheet',
	load: async () =>
		(await import('@app/features/voice/components/bottomsheets/VoiceLobbyBottomSheet')).VoiceLobbyBottomSheet,
});
const DirectCallLobbyBottomSheet = createNamedLoadableComponent<Record<string, unknown>>({
	displayName: 'DirectCallLobbyBottomSheet',
	load: async () =>
		(await import('@app/features/voice/components/bottomsheets/DirectCallLobbyBottomSheet')).DirectCallLobbyBottomSheet,
});

interface MobileBottomNavProps {
	currentUser: User;
}

export const MobileBottomNav = observer((_props: MobileBottomNavProps) => {
	const {i18n} = useLingui();
	const location = useLocation();
	const lastChannelRef = useRef<{guildId: string | null; channelId: string | null} | null>(null);
	const [voiceLobbyOpen, setVoiceLobbyOpen] = useState(false);
	const [voiceLobbyLoaded, setVoiceLobbyLoaded] = useState(false);
	const {channel: voiceChannel, guild: voiceGuild, isConnected: isConnectedToVoice} = useConnectedVoiceSession();
	const isDirectCallChannel = Boolean(
		voiceChannel && (voiceChannel.type === ChannelTypes.DM || voiceChannel.type === ChannelTypes.GROUP_DM),
	);
	useSkeletonLayoutReport(() => {
		reportSkeletonVoiceConnected(isConnectedToVoice);
	}, `${isConnectedToVoice}`);
	useEffect(() => {
		if (Routes.isChannelRoute(location.pathname)) {
			const channel = Navigation.channelId ? Channels.getChannel(Navigation.channelId) : undefined;
			if (
				channel &&
				!channel.isPrivate() &&
				(channel.type === ChannelTypes.GUILD_CATEGORY || channel.type === ChannelTypes.GUILD_LINK)
			) {
				return;
			}
			lastChannelRef.current = {guildId: Navigation.guildId, channelId: Navigation.channelId};
		}
	}, [location.pathname]);
	// Pioneerchat's four tabs, as in the design: Chats, To-dos, Activity, Search.
	const guildId = resolveHomeGuildId();
	const onTodos = guildId != null && location.pathname === Routes.guildTasks(guildId);
	const go = (path: string) => {
		if (location.pathname !== path) RouterUtils.transitionTo(path);
	};
	const tabs = [
		{
			key: 'chats',
			label: i18n._(TAB_CHATS_DESCRIPTOR),
			icon: ChatCircleIcon,
			active: Routes.isChannelRoute(location.pathname) && !onTodos,
			go: () => {
				const last = lastChannelRef.current;
				if (last?.channelId && !Routes.isChannelRoute(location.pathname)) {
					NavigationCommands.selectChannel(last.guildId ?? undefined, last.channelId);
				} else if (guildId) {
					go(Routes.guildChannel(guildId));
				}
			},
		},
		{
			key: 'todos',
			label: i18n._(TAB_TODOS_DESCRIPTOR),
			icon: CheckSquareIcon,
			active: onTodos,
			go: () => guildId && go(Routes.guildTasks(guildId)),
		},
		{
			key: 'activity',
			label: i18n._(TAB_ACTIVITY_DESCRIPTOR),
			icon: BellSimpleIcon,
			active: location.pathname === Routes.NOTIFICATIONS,
			go: () => go(Routes.NOTIFICATIONS),
		},
		{
			key: 'search',
			label: i18n._(TAB_SEARCH_DESCRIPTOR),
			icon: MagnifyingGlassIcon,
			active: false,
			go: () => QuickSwitcher.show(),
		},
	];
	const handleVoiceIndicatorPress = useCallback(() => {
		setVoiceLobbyLoaded(true);
		setVoiceLobbyOpen(true);
	}, []);
	const handleCloseVoiceLobby = useCallback(() => {
		setVoiceLobbyOpen(false);
	}, []);
	return (
		<>
			<nav
				className={styles.container}
				aria-label={i18n._(PRIMARY_NAVIGATION_LANDMARK_DESCRIPTOR)}
				data-flx="app.mobile-bottom-nav.container"
			>
				{tabs.map((tab) => {
					const Icon = tab.icon;
					return (
						<button
							key={tab.key}
							type="button"
							onClick={tab.go}
							className={clsx(styles.navButton, tab.active ? styles.navButtonActive : styles.navButtonInactive)}
							aria-current={tab.active ? 'page' : undefined}
							data-flx={`app.mobile-bottom-nav.tab.${tab.key}`}
						>
							<Icon className={styles.icon} aria-hidden="true" data-flx="app.mobile-bottom-nav.icon" />
							<span className={styles.label} data-flx="app.mobile-bottom-nav.label">
								{tab.label}
							</span>
						</button>
					);
				})}
				{isConnectedToVoice && (
					<button
						type="button"
						onClick={handleVoiceIndicatorPress}
						className={clsx(styles.navButton, styles.voiceButton)}
						aria-pressed={voiceLobbyOpen}
						data-flx="app.mobile-bottom-nav.nav-button.voice-indicator-press"
					>
						<SpeakerHighIcon className={styles.icon} data-flx="app.mobile-bottom-nav.icon--2" />
						<span className={styles.label} data-flx="app.mobile-bottom-nav.label--2">
							<Trans>Voice</Trans>
						</span>
					</button>
				)}
			</nav>
			{isConnectedToVoice && voiceChannel && voiceGuild && !isDirectCallChannel && voiceLobbyLoaded && (
				<VoiceLobbyBottomSheet
					isOpen={voiceLobbyOpen}
					onClose={handleCloseVoiceLobby}
					channel={voiceChannel}
					guild={voiceGuild}
					data-flx="app.mobile-bottom-nav.voice-lobby-bottom-sheet"
				/>
			)}
			{isConnectedToVoice && voiceChannel && isDirectCallChannel && voiceLobbyLoaded && (
				<DirectCallLobbyBottomSheet
					isOpen={voiceLobbyOpen}
					onClose={handleCloseVoiceLobby}
					channel={voiceChannel}
					data-flx="app.mobile-bottom-nav.direct-call-lobby-bottom-sheet"
				/>
			)}
		</>
	);
});
